// grid-filter.component.ts
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputComponent } from '../../../../forms/input/input.component';
import { SelectComponent } from '../../../../forms/select/select.component';
import { DatepickerComponent } from '../../../../forms/datepicker/datepicker.component';

export interface FilterFieldConfig {
  id: string;
  controlType: 'input' | 'select' | 'datepicker' | 'daterange';
  formControlName: string;
  config: {
    label?: string;
    placeholder?: string;
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
    icon?: string;
    required?: boolean;
    disabled?: boolean;
    hint?: string;
    data?: any[];
    textField?: string;
    valueField?: string;
    defaultItem?: any;
    valuePrimitive?: boolean;
    format?: string;
    min?: Date;
    max?: Date;
  };
  validation?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    errorMessages?: { [key: string]: string };
  };
  defaultValue?: any;
  width?: string;
  className?: string;
  debounceTime?: number;
  applyTo?: string[];
  operator?: 'eq' | 'neq' | 'contains' | 'startswith' | 'endswith' | 'gte' | 'lte' | 'gt' | 'lt';
  logic?: 'and' | 'or';
  gridPosition?: {
    colSpan?: number;
    colClass?: string;
  };
}

export interface FilterChangeEvent {
  fieldId: string;
  value: any;
  allFilters: { [key: string]: any };
  kendoFilter: CompositeFilterDescriptor | null;
}

@Component({
  selector: 'app-grid-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonsModule,
    InputComponent,
    SelectComponent,
    DatepickerComponent,
  ],
  templateUrl: './grid-filter.component.html',
  styleUrls: ['./grid-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFilterComponent implements OnInit, OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly filterChange$ = new Subject<{
    field: FilterFieldConfig;
    value: any;
  }>();

  @Input() fields: FilterFieldConfig[] = [];
  @Input() disabled = false;
  @Input() showLabels = false;
  @Input() showClearButton = true;
  @Input() showClearButtonText = true;
  @Input() layout: 'horizontal' | 'vertical' = 'horizontal';
  @Input() initialValues: { [key: string]: any } = {};
  @Input() globalDebounceTime = 500;

  @Output() filterChange = new EventEmitter<FilterChangeEvent>();
  @Output() filterClear = new EventEmitter<void>();

  form!: FormGroup;
  hasActiveFilters = false;

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']) {
      const tempData = JSON.parse(JSON.stringify(this.fields));
      this.fields = tempData;
      this.initializeForm();
      this.setupFormSubscriptions();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize form with controls
   */
  private initializeForm(): void {
    const controls: { [key: string]: any } = {};

    this.fields.forEach((field) => {
      const initialValue = this.getInitialValue(field);
      controls[field.formControlName] = [
        { value: initialValue, disabled: this.disabled ?? field.config?.disabled },
      ];

      // For daterange, create start and end controls
      if (field.controlType === 'daterange') {
        controls[field.formControlName + 'Start'] = [
          {
            value: this.initialValues[field.formControlName + 'Start'] ?? null,
            disabled: this.disabled ?? field.config?.disabled,
          },
        ];
        controls[field.formControlName + 'End'] = [
          {
            value: this.initialValues[field.formControlName + 'End'] ?? null,
            disabled: this.disabled ?? field.config?.disabled,
          },
        ];
      }
    });

    this.form = this.fb.group(controls);
    this.updateActiveFiltersStatus();
    this.cdr.markForCheck();
  }

  /**
   * Get initial value for field
   */
  private getInitialValue(field: FilterFieldConfig): any {
    if (this.initialValues && field.formControlName in this.initialValues) {
      return this.initialValues[field.formControlName];
    }
    return field.defaultValue ?? null;
  }

  /**
   * Setup form subscriptions
   */
  private setupFormSubscriptions(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateActiveFiltersStatus();
      this.cdr.markForCheck();
    });

    this.filterChange$
      .pipe(
        debounceTime(this.globalDebounceTime),
        distinctUntilChanged(
          (prev, curr) => prev.field.id === curr.field.id && prev.value === curr.value
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ field, value }) => {
        this.emitFilterChange(field, value);
      });
  }

  /**
   * Handle field value change
   */
  onFieldValueChange(field: FilterFieldConfig, value: any): void {
    const debounceTime = field.debounceTime ?? this.globalDebounceTime;
    const shouldDebounce = field.controlType === 'input' && field.config?.type !== 'number';

    if (shouldDebounce && debounceTime > 0) {
      this.filterChange$.next({ field, value });
    } else {
      this.emitFilterChange(field, value);
    }
  }

  /**
   * Emit filter change event
   */
  private emitFilterChange(field: FilterFieldConfig, value: any): void {
    const allFilters = this.form.getRawValue();
    const kendoFilter = this.buildKendoFilter();

    this.filterChange.emit({
      fieldId: field.formControlName,
      value: this.formatValue(value),
      allFilters: allFilters,
      kendoFilter: kendoFilter,
    });
  }

  /**
   * Build Kendo filter descriptor
   */
  private buildKendoFilter(): CompositeFilterDescriptor | null {
    const filters: FilterDescriptor[] = [];
    const formValue = this.form.getRawValue();

    this.fields.forEach((field) => {
      const value = formValue[field.formControlName];

      // Handle daterange separately
      if (field.controlType === 'daterange') {
        const startValue = formValue[field.formControlName + 'Start'];
        const endValue = formValue[field.formControlName + 'End'];

        if (startValue || endValue) {
          const dateFilters = this.buildDateRangeFilter(field, startValue, endValue);
          filters.push(...dateFilters);
        }
        return;
      }

      // Skip if no value
      if (this.isEmptyValue(value)) {
        return;
      }

      // Get the operator
      const operator = this.getOperatorForField(field);

      // Get fields to apply filter to
      const fieldsToApply = field.applyTo ?? [field.formControlName];

      // If multiple fields with OR logic
      if (fieldsToApply.length > 1) {
        const logic = field.logic ?? 'or';

        if (logic === 'or') {
          const subFilters: FilterDescriptor[] = fieldsToApply.map((fieldName) => ({
            field: fieldName,
            operator: operator,
            value: this.formatValue(value),
          }));

          filters.push({
            logic: 'or',
            filters: subFilters,
          } as any);
        } else {
          fieldsToApply.forEach((fieldName) => {
            filters.push({
              field: fieldName,
              operator: operator,
              value: this.formatValue(value),
            });
          });
        }
      } else {
        filters.push({
          field: fieldsToApply[0],
          operator: operator,
          value: this.formatValue(value),
        });
      }
    });

    if (filters.length === 0) {
      return null;
    }

    return {
      logic: 'and',
      filters: filters,
    };
  }

  /**
   * Build date range filter
   */
  private buildDateRangeFilter(
    field: FilterFieldConfig,
    startValue: any,
    endValue: any
  ): FilterDescriptor[] {
    const filters: FilterDescriptor[] = [];
    const fieldsToApply = field.applyTo ?? [field.formControlName];

    fieldsToApply.forEach((fieldName) => {
      if (startValue) {
        filters.push({
          field: fieldName,
          operator: 'gte',
          value: this.formatValue(startValue),
        });
      }

      if (endValue) {
        filters.push({
          field: fieldName,
          operator: 'lte',
          value: this.formatValue(endValue),
        });
      }
    });

    return filters;
  }

  /**
   * Get operator for field type
   */
  private getOperatorForField(field: FilterFieldConfig): string {
    if (field.operator) {
      return field.operator;
    }

    switch (field.controlType) {
      case 'input':
        return field.config?.type === 'number' ? 'eq' : 'contains';
      case 'select':
        return 'eq';
      case 'datepicker':
        return 'eq';
      default:
        return 'eq';
    }
  }

  /**
   * Check if value is empty
   */
  private isEmptyValue(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  /**
   * Format value
   */
  private formatValue(value: any): any {
    if (this.isEmptyValue(value)) {
      return null;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    return value;
  }

  /**
   * Clear all filters
   */
  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    const resetValues: { [key: string]: any } = {};

    this.fields.forEach((field) => {
      resetValues[field.formControlName] = field.defaultValue ?? null;

      if (field.controlType === 'daterange') {
        resetValues[field.formControlName + 'Start'] = null;
        resetValues[field.formControlName + 'End'] = null;
      }
    });

    this.form.reset(resetValues);

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.setValue(resetValues[key] ?? null, { emitEvent: true });
        control.markAsPristine();
        control.markAsUntouched();
      }
    });

    this.updateActiveFiltersStatus();
    this.filterClear.emit();

    this.filterChange.emit({
      fieldId: 'all',
      value: null,
      allFilters: this.form.getRawValue(),
      kendoFilter: null,
    });

    this.cdr.detectChanges();

    this.cdr.markForCheck();
  }

  /**
   * Update active filters status
   */
  private updateActiveFiltersStatus(): void {
    const formValue = this.form.getRawValue();

    this.hasActiveFilters = Object.keys(formValue).some((key) => {
      const value = formValue[key];
      const field = this.fields.find(
        (f) => f.formControlName === key || key.startsWith(f.formControlName)
      );
      const defaultValue = field?.defaultValue;

      return value !== null && value !== undefined && value !== '' && value !== defaultValue;
    });
  }

  /**
   * Get grid class for field
   */
  getGridClass(field: FilterFieldConfig): string {
    if (!field.gridPosition) {
      return field.className ?? 'filter-field';
    }

    const colSpan = field.gridPosition.colSpan ?? 12;
    const customClass = field.gridPosition.colClass ?? '';
    const baseClass = field.className ?? 'filter-field';

    return `${baseClass} col-md-${colSpan} ${customClass}`.trim();
  }

  /**
   * Get field width style
   */
  getFieldWidth(field: FilterFieldConfig): { [key: string]: string } {
    if (field.width) {
      return { width: field.width };
    }
    return {};
  }

  /**
   * Public methods
   */
  setFilterValue(fieldId: string, value: any): void {
    const control = this.form.get(fieldId);
    if (control) {
      control.setValue(value);
      this.cdr.markForCheck();
    }
  }

  getFilterValue(fieldId: string): any {
    return this.form.get(fieldId)?.value ?? null;
  }

  reset(): void {
    this.initializeForm();
  }

  hasFilterValue(fieldId: string): boolean {
    const value = this.form.get(fieldId)?.value;
    return value !== null && value !== undefined && value !== '';
  }

  getKendoFilter(): CompositeFilterDescriptor | null {
    return this.buildKendoFilter();
  }

  getForm(): FormGroup {
    return this.form;
  }
}
